// Supply Purchase Order Inbox — Phase 1B §P1B-5 Task 7.3 (wave-13-day-7).
//
// Surface: supply shell only. Each persona (Mehmet / Hakan / Aybars) sees
// ONLY their own POs per D-5 Ring 2 isolation. The handler enforces this on
// every read + mutation; the UI mirrors the discipline by querying with the
// current persona from useSupplyPersonaStore and re-querying on persona
// switch.
//
// Status-conditional actions per row:
//   sent          → [Acknowledge]
//   acknowledged  → [Mark Shipped] (dialog: tracking + ETA + note)
//   *             → [Open Dispute Thread] placeholder (V1.5+ defer; admin
//                    dispute route is admin-only via shell, so we render a
//                    disabled hint button rather than link cross-shell)
//
// Drive upload (V1.5+) is a disabled hint button per row.
//
// Cross-actor toast pair lives in this file (parity with admin toast helpers
// in PurchaseOrderAuthoring/QC). Consolidation to a shared `lib/po-toasts.ts`
// deferred to Group 9 closure.
//
// Wave-4 Day-4: Wave-13 Phase 1B pure-EN outlier → bilingual via
// SUPPLY_PO_INBOX_STRINGS. Mehmet TR persona consistency restored.

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Inbox, PackageCheck, Truck, MessageSquare, Upload } from 'lucide-react'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { fetchMe } from '../../lib/auth'
import { formatDate } from '../../lib/datetime'
import { toast } from '../../lib/toast'
import { Button } from '../../components/ui/button'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { POStatusBadge } from '../../components/POStatusBadge'
import { SUPPLY_PERSONAS, useSupplyPersonaStore } from '../../store/role'
import type { POHeader } from '../../mocks/data/inventory/purchase-orders'
import { pickT, useT } from '../../i18n'
import { useLocaleStore } from '../../store/locale'
import { SUPPLY_PO_INBOX_STRINGS } from '../../i18n/supply/po-inbox'
import { DataTableShell } from '../../components/ui/data-table-shell'

type InboxResponse = {
  data: POHeader[]
  meta: { total: number; persona: string }
}

function fmtDate(iso: string | null, emDash: string): string {
  if (!iso) return emDash
  return formatDate(iso, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

// Cross-actor toast pair — admin sees inbound notification of vendor action;
// supply persona sees a success self-confirmation. Pattern parity with
// emitPOSubmittedToasts / emitPOQCToasts (admin-side helpers).
//
// Locale-aware variants per Wave-4 Day-4 bilingual switch. Toast emission
// reads the current locale from useLocaleStore.getState() (non-hook context).
function emitPOAcknowledgedToasts(po: POHeader, vendorName: string): void {
  const t = pickT(SUPPLY_PO_INBOX_STRINGS, useLocaleStore.getState().locale)
  toast.info(t.toasts.acknowledgedAdminTitleFormat(vendorName, po.po_number), {
    description: t.toasts.acknowledgedAdminDescFormat(
      po.total_qty,
      po.payment_terms,
    ),
    audience: 'admin',
  })
  toast.success(t.toasts.acknowledgedSupplyTitleFormat(po.po_number), {
    description: t.toasts.acknowledgedSupplyDesc,
    audience: 'supply',
  })
}

function emitPOShippedToasts(
  po: POHeader,
  vendorName: string,
  tracking: string,
  eta: string,
): void {
  const t = pickT(SUPPLY_PO_INBOX_STRINGS, useLocaleStore.getState().locale)
  toast.info(t.toasts.shippedAdminTitleFormat(vendorName, po.po_number), {
    description: t.toasts.shippedAdminDescFormat(tracking, eta),
    audience: 'admin',
  })
  toast.success(t.toasts.shippedSupplyTitleFormat(po.po_number), {
    description: `${tracking}`,
    audience: 'supply',
  })
}

type ShipDialogState = {
  open: boolean
  po: POHeader | null
}

export function SupplyPurchaseOrderInbox() {
  const persona = useSupplyPersonaStore((s) => s.supplyPersona)
  const personaInfo = SUPPLY_PERSONAS[persona]
  const qc = useQueryClient()
  const t = useT(SUPPLY_PO_INBOX_STRINGS)

  // Real-engine: the header identity is the logged-in JWT org, NOT the maket
  // persona switcher — otherwise a real vendor (e.g. Fraz / KTM SO2) sees the
  // demo persona name (Aybars SO3). Maket default unchanged (query disabled).
  const realMe = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: fetchMe,
    enabled: USE_REAL_API,
    staleTime: 5 * 60 * 1000,
  })
  const displayName = USE_REAL_API
    ? (realMe.data?.active_membership?.persona_label ??
        realMe.data?.user.display_name ??
        personaInfo.org_name)
    : personaInfo.org_name
  const displayType = USE_REAL_API
    ? (realMe.data?.active_membership?.org_type ?? personaInfo.org_type)
    : personaInfo.org_type

  const inboxQuery = useQuery({
    queryKey: ['supply-po-inbox', persona],
    queryFn: async (): Promise<InboxResponse> => {
      const res = await fetch(apiUrl(`/supply/po-inbox?persona=${persona}`))
      if (!res.ok) throw new Error('Failed to load PO inbox')
      return (await res.json()) as InboxResponse
    },
    retry: false,
  })

  const ackMutation = useMutation({
    mutationFn: async (po: POHeader): Promise<POHeader> => {
      const res = await fetch(apiUrl(`/supply/po/${po.id}/acknowledge`), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ persona }),
      })
      if (!res.ok) throw new Error('Acknowledge failed')
      return (await res.json()) as POHeader
    },
    onSuccess: (updated, po) => {
      emitPOAcknowledgedToasts(updated, personaInfo.org_name)
      qc.invalidateQueries({ queryKey: ['supply-po-inbox', persona] })
      void po
    },
    onError: () => {
      toast.error(t.toasts.acknowledgeFailed, { audience: 'supply' })
    },
  })

  const [shipDialog, setShipDialog] = useState<ShipDialogState>({
    open: false,
    po: null,
  })
  const [trackingInput, setTrackingInput] = useState('')
  const [etaInput, setEtaInput] = useState('')
  const [shipNote, setShipNote] = useState('')

  const shipMutation = useMutation({
    mutationFn: async (args: {
      po: POHeader
      tracking_number: string
      eta: string
      note: string
    }): Promise<POHeader> => {
      const res = await fetch(apiUrl(`/supply/po/${args.po.id}/ship`), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          persona,
          tracking_number: args.tracking_number,
          eta: args.eta,
          ...(args.note ? { note: args.note } : {}),
        }),
      })
      if (!res.ok) throw new Error('Ship failed')
      return (await res.json()) as POHeader
    },
    onSuccess: (updated, vars) => {
      emitPOShippedToasts(
        updated,
        personaInfo.org_name,
        vars.tracking_number,
        vars.eta,
      )
      setShipDialog({ open: false, po: null })
      setTrackingInput('')
      setEtaInput('')
      setShipNote('')
      qc.invalidateQueries({ queryKey: ['supply-po-inbox', persona] })
    },
    onError: () => {
      toast.error(t.toasts.shipFailed, { audience: 'supply' })
    },
  })

  function openShipDialog(po: POHeader): void {
    setShipDialog({ open: true, po })
    setTrackingInput('')
    setEtaInput('')
    setShipNote('')
  }

  function confirmShip(): void {
    if (!shipDialog.po) return
    if (!trackingInput.trim() || !etaInput.trim()) return
    shipMutation.mutate({
      po: shipDialog.po,
      tracking_number: trackingInput.trim(),
      eta: etaInput.trim(),
      note: shipNote.trim(),
    })
  }

  const rows = useMemo(() => inboxQuery.data?.data ?? [], [inboxQuery.data])

  return (
    <div className="max-w-6xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-[var(--color-text)]">
          {t.pageTitleFormat(displayName)}
        </h1>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">
          {t.descriptionFormat(displayName, displayType)}
        </p>
      </header>

      {inboxQuery.isLoading ? (
        <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-sm text-[var(--color-text-muted)]">
          {t.loading}
        </div>
      ) : inboxQuery.isError ? (
        <div className="flex items-center gap-3 rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-sm text-[var(--color-text-muted)]">
          <Inbox className="h-5 w-5 text-[var(--color-text-muted)]" />
          <span>{t.emptyState.descriptionFormat(displayName)}</span>
        </div>
      ) : rows.length === 0 ? (
        <div className="flex items-center gap-3 rounded-md border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-8 text-sm text-[var(--color-text-muted)]">
          <Inbox className="h-5 w-5 text-[var(--color-text-muted)]" />
          <span>{t.emptyState.descriptionFormat(displayName)}</span>
        </div>
      ) : (
        <DataTableShell>
          <table>
            <thead>
              <tr>
                <th>
                  {t.table.columnHeaders.poNumber}
                </th>
                <th>
                  {t.table.columnHeaders.createdAt}
                </th>
                <th className="text-right">
                  {t.table.columnHeaders.totalQty}
                </th>
                <th>
                  {t.table.columnHeaders.status}
                </th>
                <th className="text-right">
                  {t.table.columnHeaders.actions}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((po) => (
                <tr key={po.id}>
                  <td className="py-3 font-medium text-[var(--color-text)]">
                    {po.po_number}
                  </td>
                  <td className="py-3 text-[var(--color-text-muted)]">
                    {fmtDate(po.created_at, t.emDash)}
                  </td>
                  <td className="py-3 text-right tabular-nums text-[var(--color-text)]">
                    {po.total_qty}
                  </td>
                  <td className="py-3">
                    <POStatusBadge status={po.status} />
                  </td>
                  <td className="py-3">
                    <div className="flex items-center justify-end gap-2">
                      {po.status === 'sent' && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => ackMutation.mutate(po)}
                          disabled={ackMutation.isPending}
                        >
                          <PackageCheck className="mr-1 h-4 w-4" />
                          {t.actions.acknowledge}
                        </Button>
                      )}
                      {po.status === 'acknowledged' && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => openShipDialog(po)}
                        >
                          <Truck className="mr-1 h-4 w-4" />
                          {t.actions.markShipped}
                        </Button>
                      )}
                      {(po.status === 'partial_received' ||
                        po.status === 'received' ||
                        po.status === 'disputed') && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled
                          title={t.actions.openDisputeTitle}
                        >
                          <MessageSquare className="mr-1 h-4 w-4" />
                          {t.actions.openDispute}
                        </Button>
                      )}
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        disabled
                        title={t.actions.uploadTitle}
                      >
                        <Upload className="mr-1 h-4 w-4" />
                        {t.actions.upload}
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DataTableShell>
      )}

      <Dialog
        open={shipDialog.open}
        onOpenChange={(open) =>
          setShipDialog((s) => ({ open, po: open ? s.po : null }))
        }
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t.shipDialog.titleFormat(shipDialog.po?.po_number ?? '')}
            </DialogTitle>
            <DialogDescription>
              {t.shipDialog.description}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-[var(--color-text)]">
                {t.shipDialog.trackingLabel}
              </span>
              <input
                type="text"
                value={trackingInput}
                onChange={(e) => setTrackingInput(e.target.value)}
                aria-label={t.shipDialog.trackingLabel}
                className="h-9 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
                placeholder={t.shipDialog.trackingPlaceholder}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-[var(--color-text)]">
                {t.shipDialog.etaLabel}
              </span>
              <input
                type="date"
                value={etaInput}
                onChange={(e) => setEtaInput(e.target.value)}
                aria-label={t.shipDialog.etaLabel}
                className="h-9 rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-[var(--color-text)]">
                {t.shipDialog.noteLabel}
              </span>
              <textarea
                value={shipNote}
                onChange={(e) => setShipNote(e.target.value)}
                rows={3}
                className="rounded-md border border-[var(--color-border-strong)] bg-[var(--color-surface)] px-2 py-1 text-[var(--color-text)] focus-visible:shadow-[var(--shadow-focus)]"
                placeholder={t.shipDialog.notePlaceholder}
              />
            </label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShipDialog({ open: false, po: null })}
            >
              {t.shipDialog.cancel}
            </Button>
            <Button
              type="button"
              onClick={confirmShip}
              disabled={
                !trackingInput.trim() ||
                !etaInput.trim() ||
                shipMutation.isPending
              }
            >
              {t.shipDialog.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
